import type { SelectionOp } from 'Models/facetRequest';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import type { RequestSelectionItem } from 'Modules/serverApi/types';
import { groupByField, uniqBy } from 'Utils/array';
import { isTruthy } from 'Utils/guards';

export const RELEVANCE_SORT = 'relevance';

interface ConvertibleEntry extends RequestExtra {
  field?: string;
  fieldName?: string;
  selection?: Array<string | undefined>;
  op?: Array<SelectionOp | undefined>;
  expand?: Array<boolean | undefined>;
  hidden?: Array<boolean | undefined>;
  maxCount?: number;
  descending?: boolean;
}

interface ConvertibleMap extends RequestExtra {
  [key: string]: ConvertibleEntry;
}

export function distinctSelection<T extends { field: string; term?: string }>(selection: T[]): T[] {
  return uniqBy(selection, (facet) => [facet.field, facet.term].join());
}

export function facetsToConvertible(
  {
    maxCountByField = {},
    selection: userSelection = [],
  }: { maxCountByField?: Partial<Record<string, number>>; selection?: RequestSelectionItem[] },
  { selection: defaultSelection = [] }: { selection?: RequestSelectionItem[] } = {},
): ConvertibleMap | undefined {
  const selection = distinctSelection([...userSelection, ...defaultSelection].filter(isTruthy));
  const fieldsWithExtraParams = Object.keys({ ...maxCountByField });

  if (!selection.length && !fieldsWithExtraParams.length) {
    return;
  }

  const fieldIndexes: Record<string, number> = {};

  let i = 0;

  const convertible: ConvertibleMap = {};
  const fieldToSelection = groupByField(selection, 'field');
  Object.entries(fieldToSelection).forEach(([field, values]) => {
    fieldIndexes[field] = i;
    convertible[i++] = {
      field: encodeURIComponent(field),
      selection: values.map((v) => v.term && encodeURIComponent(v.term)),
      op: values.map((v) => v.op),
      expand: values.map((v) => v.expand),
      hidden: values.map((v) => v.hidden),
    };
  });

  for (const field of fieldsWithExtraParams) {
    const { [field]: j = -1 } = fieldIndexes;

    if (j < 0) {
      convertible[i++] = {
        field: encodeURIComponent(field),
        maxCount: maxCountByField[field],
      };
    } else {
      const entry = convertible[String(j)];
      if (entry) {
        entry.maxCount = maxCountByField[field];
      }
    }
  }

  return convertible;
}

interface SortToken {
  fieldName: string;
  descending: boolean;
}

/**
 * Parse a comma-separated sort string (e.g. `"price:desc,name"`) into
 * explicit tokens. Tokens with an empty `fieldName` (e.g. `":desc"`) are
 * dropped so downstream consumers never emit `field=` with no value.
 */
export function parseSortTokens(sort: string): SortToken[] {
  const result: SortToken[] = [];
  for (const raw of sort.split(',')) {
    const [fieldName = '', descending] = raw.split(':');
    if (!fieldName) {
      continue;
    }
    result.push({ fieldName, descending: descending?.toLowerCase() === 'desc' });
  }
  return result;
}

export function sortToConvertible(sort: string | null | undefined): ConvertibleMap | undefined {
  if (!sort || sort === RELEVANCE_SORT) {
    return;
  }
  const tokens = parseSortTokens(sort);
  if (!tokens.length) {
    return;
  }
  return tokens.reduce<ConvertibleMap>((sortObj, { fieldName, descending }, i) => {
    sortObj[i] = { fieldName, descending };
    return sortObj;
  }, {});
}
